import React, { useContext } from 'react';

import { Box, Button } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import noop from 'lodash/noop';

import { ActivateSubscriptionDialog, FilterSystem, NoAccess } from '@vyce/core/src/components';
import { useLocationUsersData } from '@vyce/core/src/modules/timeModule/hooks';
import { LocationUsers } from '@vyce/core/src/views/time/components';
import { DeviceContext } from '@vyce/core/src/contexts';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { TIME_INTERFACE_PERMISSIONS } from '@vyce/core/src/types';

import { UniversalInviteModule } from '../../modules';
import { useInviteButton, useTypedSelector } from '../../hooks';
import { ChatUser } from '@vyce/core/src/types';

export const LocationsUsers: React.FC = () => {
  const { selectedCompany, mainDashboardFilters } = useTypedSelector(state => state.helper);
  const { first_name, last_name, email, phone, photo, uuid } = useTypedSelector(state => state.user);
  const { isMobile } = useContext(DeviceContext);
  const { canAnywhere, ready } = usePermissions();

  const chatUser: ChatUser = {
    uid: uuid,
    name: `${first_name} ${last_name}`,
    email,
    phone,
    avatarUrl: photo?.url,
  };

  const {
    workers,
    total,
    sortModel,
    loadingTableData,
    filters,
    filtersSections,
    handlePageChange,
    handleSearchChange,
    handleFilterChange,
    handleSortModelChange,
    setOffset,
    removeWorkerFromAllLocations,
  } = useLocationUsersData({ siteId: undefined, selectedCompany, mainDashboardFilters });

  const {
    isActivateSubscriptionDialogOpen,
    isInviteFormOpen,
    subscribeLoading,
    closeActivateSubscriptionDialog,
    setCloseInviteForm,
    handleInviteButtonClick,
    simpleSubscribe,
    canInvite,
  } = useInviteButton();

  // The tab is hidden without the right, but /time/users stays a reachable URL —
  // an empty table there would read as a bug rather than a missing permission.
  if (ready && !canAnywhere(TIME_INTERFACE_PERMISSIONS.LOOKUP_WORKERS)) {
    return <NoAccess title="You don’t have access to Time users" />;
  }

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={3}>
        <AppSearchInput onChange={handleSearchChange} isSmall fullWidth smallRounding expanded={!isMobile} />

        <Box display="flex" alignItems="center" gridGap={16}>
          {canInvite && (
            <Button
              onClick={handleInviteButtonClick}
              size="small"
              data-tour="invite"
              startIcon={<AiOutlinePlus />}
              variant="contained"
              color="primary">
              Invite New Members
            </Button>
          )}
          {filtersSections && (
            <FilterSystem
              selectedValues={filters}
              filtersSections={filtersSections}
              onFiltersChange={handleFilterChange || noop}
            />
          )}
        </Box>
      </Box>

      <LocationUsers
        chatCreator={chatUser}
        companyId={selectedCompany?.uuid}
        loading={loadingTableData}
        total={total}
        sortModel={sortModel}
        tableHeight="calc(100vh - 270px)"
        handleSortModelChange={handleSortModelChange}
        handlePageChange={handlePageChange}
        workers={workers}
        setOffset={setOffset}
        removeWorkerFromAllLocations={removeWorkerFromAllLocations}
      />

      <UniversalInviteModule isOpen={isInviteFormOpen} onClose={setCloseInviteForm} />

      <ActivateSubscriptionDialog
        open={isActivateSubscriptionDialogOpen}
        onClose={closeActivateSubscriptionDialog}
        callback={simpleSubscribe}
        loading={subscribeLoading}
        wasSubscribed={selectedCompany?.subscribed_company}
      />
    </>
  );
};
